'use client';

import { useEffect, useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useOnboardingStore } from '../store';
import { ProgressIndicator } from './shared/ProgressIndicator';
import { AccountStep } from './steps/AccountStep';
import { BrandStep } from './steps/BrandStep';
import { GoalsStep } from './steps/GoalsStep';
import { ConnectStep } from './steps/ConnectStep';
import { PreviewStep } from './steps/PreviewStep';
import {
  extractStoreForOnboarding,
  generateOnboardingForm,
  completeOnboarding,
} from '../actions';

interface InviteContext {
  userId: string;
  companyId: string;
  companyName: string;
  isAgency: boolean;
  userType: string | null;
  klaviyoConnected: boolean;
}

interface OnboardingWizardProps {
  inviteContext: InviteContext | null;
}

const STEPS = ['account', 'brand', 'goals', 'connect', 'preview'] as const;

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 200 : -200,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction < 0 ? 200 : -200,
    opacity: 0,
  }),
};

export function OnboardingWizard({ inviteContext }: OnboardingWizardProps) {
  const store = useOnboardingStore();
  const directionRef = useRef(1);
  const pipelineRunningRef = useRef(false);
  const [hydrated, setHydrated] = useState(false);

  // Wait for Zustand to hydrate from localStorage before rendering
  useEffect(() => {
    setHydrated(true);
  }, []);

  // Reset to step 1 when ?new=1 is in the URL (e.g. from "Sign up" link)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('new') === '1') {
        localStorage.removeItem('kinetic-onboarding');
        store.reset();
        // Clean the URL
        window.history.replaceState({}, '', '/onboarding');
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize invited user context (once on mount)
  useEffect(() => {
    if (inviteContext) {
      store.setInvitedUser(true, inviteContext.klaviyoConnected);
      store.setAuthData({
        userId: inviteContext.userId,
        companyId: inviteContext.companyId,
      });
      if (inviteContext.companyName) {
        store.setBrandData({
          brandName: inviteContext.companyName,
          shopUrl: store.shopUrl,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine which steps to show
  const getVisibleSteps = useCallback(() => {
    const steps = [...STEPS];
    // Skip Klaviyo connect for invited users with existing connection, or agencies
    if ((store.isInvitedUser && store.existingKlaviyoConnected) || store.userType === 'agency') {
      return steps.filter((s) => s !== 'connect');
    }
    return steps;
  }, [store.isInvitedUser, store.existingKlaviyoConnected, store.userType]);

  const visibleSteps = getVisibleSteps();
  const currentStepName = visibleSteps[store.currentStep] || 'account';

  const goNext = useCallback(() => {
    const nextStep = store.currentStep + 1;
    if (nextStep < visibleSteps.length) {
      directionRef.current = 1;
      store.completeStep(store.currentStep);
      store.setStep(nextStep);
    }
  }, [store, visibleSteps.length]);

  const goBack = useCallback(() => {
    if (store.currentStep > 0) {
      directionRef.current = -1;
      store.setStep(store.currentStep - 1);
    }
  }, [store]);

  // Background AI pipeline: extract store when shop URL is submitted
  const triggerAiPipeline = useCallback(
    async (shopUrl: string) => {
      if (!shopUrl || pipelineRunningRef.current) return;
      pipelineRunningRef.current = true;

      store.setAiGenerationStatus('extracting');
      store.setAiGenerationError(null);

      try {
        const extractResult = await extractStoreForOnboarding(shopUrl);
        if (!extractResult.success) {
          store.setAiGenerationStatus('error');
          store.setAiGenerationError('error' in extractResult ? extractResult.error : 'Extraction failed');
          pipelineRunningRef.current = false;
          return;
        }

        store.setStoreData(extractResult.data);
        store.setAiGenerationStatus('generating');

        // If use cases are already selected, generate immediately
        if (store.useCases.length > 0 && store.companyId && store.userId) {
          const genResult = await generateOnboardingForm(
            store.companyId,
            store.userId,
            extractResult.data,
            store.useCases
          );
          if (genResult.success) {
            store.setGeneratedFormId(genResult.formId);
            store.setAiGenerationStatus('done');
          } else {
            store.setAiGenerationStatus('error');
            store.setAiGenerationError(genResult.error);
          }
        }
        // Otherwise, generation will be triggered by GoalsStep or PreviewStep
      } catch {
        store.setAiGenerationStatus('error');
        store.setAiGenerationError('Something went wrong');
      } finally {
        pipelineRunningRef.current = false;
      }
    },
    [store]
  );

  // When goals are submitted and store data is ready, trigger form generation
  const handleGoalsComplete = useCallback(async () => {
    goNext();

    // Trigger generation if extraction is done but form hasn't been generated yet
    if (
      store.storeData &&
      store.useCases.length > 0 &&
      store.companyId &&
      store.userId &&
      store.aiGenerationStatus === 'generating' &&
      !store.generatedFormId
    ) {
      const genResult = await generateOnboardingForm(
        store.companyId,
        store.userId,
        store.storeData,
        store.useCases
      );
      if (genResult.success) {
        store.setGeneratedFormId(genResult.formId);
        store.setAiGenerationStatus('done');
      } else {
        store.setAiGenerationStatus('error');
        store.setAiGenerationError(genResult.error);
      }
    }
  }, [store, goNext]);

  const [finishing, setFinishing] = useState(false);

  const handleFinish = useCallback(
    async (action: 'customize' | 'scratch' | 'skip') => {
      setFinishing(true);

      if (store.companyId) {
        await completeOnboarding(store.companyId);
      }

      // Ensure auth cookie is set before navigating to authenticated pages
      const { createClient: createSupabase } = await import('@/utils/supabase/client');
      const supabase = createSupabase();
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        // Try refreshing the session to ensure cookie is written
        await supabase.auth.refreshSession();
      }

      const formId = store.generatedFormId;
      const url = action === 'customize' && formId
        ? `/dashboard/form/${formId}`
        : action === 'scratch'
          ? '/dashboard/forms?new=1'
          : '/dashboard';

      localStorage.removeItem('kinetic-onboarding');
      window.location.href = url;
    },
    [store]
  );

  const renderStep = () => {
    switch (currentStepName) {
      case 'account':
        return <AccountStep onNext={goNext} isInvitedUser={store.isInvitedUser} />;
      case 'brand':
        return (
          <BrandStep
            onNext={goNext}
            onBack={goBack}
            onShopUrlSubmit={triggerAiPipeline}
            isInvitedUser={store.isInvitedUser}
          />
        );
      case 'goals':
        return <GoalsStep onNext={handleGoalsComplete} onBack={goBack} />;
      case 'connect':
        return <ConnectStep onNext={goNext} onBack={goBack} />;
      case 'preview':
        return <PreviewStep onFinish={handleFinish} onBack={goBack} finishing={finishing} currentStep={store.currentStep} totalSteps={visibleSteps.length} onReveal={() => setPreviewRevealed(true)} />;
      default:
        return null;
    }
  };

  // Only use split-screen after email is confirmed (PreviewStep handles this internally)
  const [previewRevealed, setPreviewRevealed] = useState(false);
  const isSplitScreen = currentStepName === 'preview' && previewRevealed;

  // Step metadata for the left branding panel
  const stepMeta: Record<string, { title: string; subtitle: string }> = {
    account: store.isAuthenticated
      ? { title: 'Tell us who you are', subtitle: 'This helps us tailor your experience.' }
      : { title: 'Welcome to Kinetic!', subtitle: "Let's get you set up in under a minute." },
    brand: store.userType === 'agency'
      ? { title: 'Tell us about your agency', subtitle: 'This helps us personalize your experience.' }
      : { title: 'Tell us about your brand', subtitle: 'This helps us personalize your experience.' },
    goals: {
      title: 'What are your goals?',
      subtitle: 'Pick as many as you want.',
    },
    connect: {
      title: 'Connect Klaviyo',
      subtitle: 'Load your email templates and sync form responses.',
    },
    preview: {
      title: 'One last step',
      subtitle: 'Confirm your email to access your dashboard.',
    },
  };

  const meta = stepMeta[currentStepName] || stepMeta.account;

  // Don't render until Zustand hydrates from localStorage
  if (!hydrated) {
    return <div className="min-h-svh bg-[#fef0ff]" />;
  }

  // Preview step with form has its own split layout
  if (isSplitScreen) {
    return (
      <div className="min-h-svh">
        {renderStep()}
      </div>
    );
  }

  return (
    <div className="flex min-h-svh">
      {/* Left: Branding + step headline */}
      <div className="hidden lg:flex lg:w-2/5 bg-primary flex-col justify-between p-10 items-end text-right">
        <div className="w-full flex justify-end">
          <img src="/images/kinetic-logo-full.svg" alt="Kinetic" className="h-8 brightness-0 invert opacity-80" />
        </div>
        <div className="space-y-3">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStepName}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-3"
            >
              <h2 className="text-3xl font-bold text-primary-foreground tracking-tight">{meta.title}</h2>
              <p className="text-primary-foreground/60 text-base">{meta.subtitle}</p>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="text-primary-foreground/30 text-xs">
          Step {store.currentStep + 1} of {visibleSteps.length}
        </div>
      </div>

      {/* Right: Form content */}
      <div className="w-full lg:w-3/5 flex flex-col bg-[#fef0ff]">
        {/* Mobile: progress bar (hidden on last step) */}
        {store.currentStep < visibleSteps.length - 1 && (
          <div className="lg:hidden">
            <ProgressIndicator
              currentStep={store.currentStep}
              totalSteps={visibleSteps.length}
            />
          </div>
        )}

        <div className="flex-1 flex items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-md">
            <AnimatePresence mode="wait" custom={directionRef.current}>
              <motion.div
                key={currentStepName}
                custom={directionRef.current}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
